import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { useConsent } from './ConsentContext.js';
import { defaultStrings } from './strings.js';
const categoryRowStyle = {
    padding: '12px 0',
    borderBottom: '1px solid var(--cmp-border, currentColor)',
    opacity: 0.9,
};
const categoryHeaderStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 4,
};
const categoryDescStyle = {
    fontSize: '0.85em',
    opacity: 0.7,
    marginLeft: 24,
    lineHeight: 1.4,
};
const cookieListStyle = {
    fontSize: '0.8em',
    opacity: 0.6,
    marginLeft: 24,
    marginTop: 4,
    fontStyle: 'italic',
};
export function ConsentDialog(props = {}) {
    const { core, state, strings, dialogId } = useConsent();
    const cats = strings.categories ?? defaultStrings.categories ?? {};
    const isRTL = typeof strings?.locale === 'string' && strings.locale.toLowerCase().startsWith('ar');
    const [analytics, setAnalytics] = useState(state.categories.analytics);
    const [marketing, setMarketing] = useState(state.categories.marketing);
    const [functional, setFunctional] = useState(state.categories.functional);
    const dlgRef = useRef(null);
    const firstRef = useRef(null);
    useEffect(() => {
        const el = dlgRef.current;
        if (!el)
            return;
        const onKey = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                el.close();
                return;
            }
            if (e.key === 'Tab') {
                const nodes = el.querySelectorAll('button,a,input,select,textarea,[tabindex]:not([tabindex="-1"])');
                const arr = Array.from(nodes).filter((n) => !n.hasAttribute('disabled'));
                if (arr.length === 0)
                    return;
                const first = arr[0];
                const last = arr[arr.length - 1];
                const active = document.activeElement;
                if (e.shiftKey && active === first) {
                    last.focus();
                    e.preventDefault();
                }
                else if (!e.shiftKey && active === last) {
                    first.focus();
                    e.preventDefault();
                }
            }
        };
        const onOpenChange = (mutations) => {
            for (const m of mutations) {
                if (m.type === 'attributes' && m.attributeName === 'open') {
                    if (el.open) {
                        // focus first actionable element
                        setTimeout(() => firstRef.current?.focus(), 0);
                    }
                }
            }
        };
        const obs = new MutationObserver(onOpenChange);
        obs.observe(el, { attributes: true, attributeFilter: ['open'] });
        el.addEventListener('keydown', onKey);
        return () => {
            obs.disconnect();
            el.removeEventListener('keydown', onKey);
        };
    }, []);
    const apply = () => {
        core.set({ analytics, marketing, functional }, 'dialog');
        document.getElementById(dialogId)?.close();
    };
    const dataCmp = props['data-cmp'] ?? 'cmp-dialog';
    return (_jsx("dialog", { ...props, "data-cmp": dataCmp, id: dialogId, ref: dlgRef, "aria-labelledby": "cmp-dlg-title", "aria-modal": "true", dir: isRTL ? 'rtl' : undefined, style: {
            background: 'var(--cmp-bg)',
            color: 'var(--cmp-text, inherit)',
            border: '1px solid var(--cmp-border, currentColor)',
            borderRadius: 'var(--cmp-radius, 8px)',
            maxWidth: 500,
            ...(props.style ?? {}),
        }, children: _jsxs("form", { method: "dialog", style: { padding: 16 }, onSubmit: (e) => e.preventDefault(), children: [_jsx("h3", { id: "cmp-dlg-title", children: strings.title }), _jsx("p", { style: { fontSize: '0.9em', opacity: 0.8, marginTop: 8 }, children: strings.description }), _jsxs("div", { style: { marginTop: 16 }, children: [_jsxs("div", { style: categoryRowStyle, children: [_jsxs("div", { style: categoryHeaderStyle, children: [_jsx("input", { type: "checkbox", checked: true, disabled: true }), _jsx("strong", { children: cats.essential?.label ?? 'Essential' }), _jsx("span", { style: { fontSize: '0.75em', opacity: 0.6 }, children: "(required)" })] }), _jsx("div", { style: categoryDescStyle, children: cats.essential?.description }), cats.essential?.cookies && (_jsxs("div", { style: cookieListStyle, children: ["Cookies: ", cats.essential.cookies] }))] }), _jsxs("div", { style: categoryRowStyle, children: [_jsxs("label", { style: categoryHeaderStyle, children: [_jsx("input", { type: "checkbox", checked: analytics, onChange: (e) => setAnalytics(e.target.checked) }), _jsx("strong", { children: cats.analytics?.label ?? 'Analytics' })] }), _jsx("div", { style: categoryDescStyle, children: cats.analytics?.description }), cats.analytics?.cookies && (_jsxs("div", { style: cookieListStyle, children: ["Cookies: ", cats.analytics.cookies] }))] }), _jsxs("div", { style: categoryRowStyle, children: [_jsxs("label", { style: categoryHeaderStyle, children: [_jsx("input", { type: "checkbox", checked: marketing, onChange: (e) => setMarketing(e.target.checked) }), _jsx("strong", { children: cats.marketing?.label ?? 'Marketing' })] }), _jsx("div", { style: categoryDescStyle, children: cats.marketing?.description }), cats.marketing?.cookies && (_jsxs("div", { style: cookieListStyle, children: ["Cookies: ", cats.marketing.cookies] }))] }), _jsxs("div", { style: { ...categoryRowStyle, borderBottom: 'none' }, children: [_jsxs("label", { style: categoryHeaderStyle, children: [_jsx("input", { type: "checkbox", checked: functional, onChange: (e) => setFunctional(e.target.checked) }), _jsx("strong", { children: cats.functional?.label ?? 'Functional' })] }), _jsx("div", { style: categoryDescStyle, children: cats.functional?.description }), cats.functional?.cookies && (_jsxs("div", { style: cookieListStyle, children: ["Cookies: ", cats.functional.cookies] }))] })] }), _jsxs("div", { style: { marginTop: 16, display: 'flex', gap: 8 }, children: [_jsx("button", { ref: firstRef, type: "button", onClick: apply, style: {
                                padding: '6px 12px',
                                border: 'none',
                                borderRadius: 'var(--cmp-radius, 6px)',
                                background: 'var(--cmp-btn-primary-bg)',
                                color: 'var(--cmp-btn-primary-text, inherit)',
                                cursor: 'pointer',
                            }, children: strings.buttons.save }), _jsx("button", { type: "button", onClick: () => dlgRef.current?.close(), style: {
                                padding: '6px 12px',
                                border: '1px solid var(--cmp-btn-secondary-border, currentColor)',
                                borderRadius: 'var(--cmp-radius, 6px)',
                                background: 'var(--cmp-btn-secondary-bg, transparent)',
                                color: 'var(--cmp-btn-secondary-text, inherit)',
                                cursor: 'pointer',
                            }, children: strings.buttons.cancel })] })] }) }));
}
//# sourceMappingURL=ConsentDialog.js.map